/**
 * Auth Store - Firebase Authentication State (Lazy Loaded)
 *
 * Firebase is only initialized when the first auth action is called.
 * This keeps the initial bundle small and fast.
 *
 * Automatically starts/stops subscription listener on auth changes.
 */

import { create } from 'zustand';
import type { User } from 'firebase/auth';
import { useSubscriptionStore } from './subscriptionStore';

interface AuthState {
  // State
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAnonymous: boolean;
  error: string | null;
  isInitialized: boolean;
  
  // Actions
  initAuth: () => Promise<void>;
  signInGuest: () => Promise<void>;
  signInGoogle: () => Promise<void>;
  claimProfile: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

// Track if auth listener has been set up
let authListenerUnsubscribe: (() => void) | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state - not loading until user initiates auth
  user: null,
  isLoading: false,
  isAuthenticated: false,
  isAnonymous: false,
  error: null,
  isInitialized: false,
  
  // Initialize auth listener (called on first auth action)
  initAuth: async () => {
    // Only initialize once
    if (get().isInitialized || authListenerUnsubscribe) return;
    
    set({ isLoading: true });
    
    try {
      const { onAuthStateChanged } = await import('../lib/firebase');
      authListenerUnsubscribe = await onAuthStateChanged((user) => {
        set({
          user,
          isLoading: false,
          isAuthenticated: !!user,
          isAnonymous: user?.isAnonymous ?? false,
          isInitialized: true,
        });

        // Start/stop subscription listener based on auth state
        const subStore = useSubscriptionStore.getState();
        if (user && !user.isAnonymous) {
          subStore.startListening(user.uid);
        } else {
          subStore.stopListening();
        }
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to initialize auth',
        isLoading: false,
        isInitialized: true,
      });
    }
  },
  
  // Actions
  signInGuest: async () => {
    const { initAuth } = get();
    await initAuth();
    
    try {
      set({ isLoading: true, error: null });
      const { signInAsGuest } = await import('../lib/firebase');
      await signInAsGuest();
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to sign in as guest',
        isLoading: false,
      });
    }
  },
  
  signInGoogle: async () => {
    const { initAuth } = get();
    await initAuth();
    
    try {
      set({ isLoading: true, error: null });
      const { signInWithGoogle } = await import('../lib/firebase');
      await signInWithGoogle();
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to sign in with Google',
        isLoading: false,
      });
    }
  },
  
  claimProfile: async () => {
    const { initAuth } = get();
    await initAuth();
    
    try {
      set({ isLoading: true, error: null });
      const { linkAnonymousToGoogle } = await import('../lib/firebase');
      await linkAnonymousToGoogle();
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to claim profile',
        isLoading: false,
      });
    }
  },
  
  signOut: async () => {
    try {
      set({ isLoading: true, error: null });
      const { signOut: firebaseSignOut } = await import('../lib/firebase');
      await firebaseSignOut();
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to sign out',
        isLoading: false,
      });
    }
  },
  
  clearError: () => set({ error: null }),
}));
